import * as React from 'react';
import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { usePageHeader } from 'global/PageHeaderContext';
import { useDynamicLogo } from 'hooks/useDynamicLogo';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { useTheme } from 'providers/ThemeProvider';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

const Header: React.FC = () => {
  const { theme, isDark } = useTheme();
  const { logo, isLoading: logoLoading } = useDynamicLogo();
  const [bgImage, setBgImage] = useState<string | null>(null);
  const navigate = useNavigate();

  // Lazy load header background image - does not block initial render
  useEffect(() => {
    let isMounted = true;
    const bgImport = isDark
      ? import('assets/images/header/header-background.png')
      : import('assets/images/header/header-background-light.png');

    bgImport
      .then(module => {
        if (isMounted) {
          const img = new Image();
          img.onload = () => {
            if (isMounted) setBgImage(module.default);
          };
          img.src = module.default;
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [isDark]);

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Page-level title/subtitle derived from Strapi menu data
  const { pageHeader } = usePageHeader();

  if (!theme) return null;

  return (
    <header
      className={cn(
        'flex bg-gradient-to-r from-brand-dark-bg to-brand-darker-bg transition-[padding-left] duration-300',
        'h-20',
        isMobile || isTablet ? 'px-4' : 'pl-60 pr-6'
      )}
      style={{
        backgroundImage: bgImage ? `url(${bgImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.3s ease-in-out',
      }}
    >
      {isMobile || isTablet ? (
        <div className="flex justify-between items-center w-full">
          {!logoLoading && (
            <img
              src={logo || undefined}
              alt="Logo"
              className="max-h-[4.6rem] cursor-pointer"
              onClick={() => navigate('/')}
            />
          )}
          <div className="flex items-center gap-2" />
        </div>
      ) : (
        <div className="flex justify-between w-full h-full items-center">
          <div className="pl-6">
            {pageHeader && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h1 className="text-h3 text-header-title cursor-default">
                      {pageHeader.title}
                    </h1>
                  </TooltipTrigger>
                  {pageHeader.subtitle && (
                    <TooltipContent side="bottom" align="start">
                      <p>{pageHeader.subtitle}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!logoLoading && <img src={logo || undefined} alt="Logo" className="max-h-[4.6rem]" />}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
